import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/lib/auth-context';
import * as db from '@/lib/data-service';
import { CITIES, SECTS, DIET_PRACTICES, PRACTICE_LEVELS, LANGUAGES, GENERAL_PROMPTS, JAIN_PROMPTS, RELATIONSHIP_INTENTS, FEET_OPTIONS, INCHES_OPTIONS, feetInchesToCm, cmToFeetInches } from '@/lib/constants';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { ChevronLeft, Camera, X, Plus, Check, Save, Loader2 } from 'lucide-react';
import { compressImage } from '@/lib/image-compress';
import { uploadToStorage } from '@/lib/supabase';
import { toast } from 'sonner';
import TraitsPicker from '@/components/TraitsPicker';
import HobbiesPicker from '@/components/HobbiesPicker';

export default function EditProfile() {
  const { user, profile, refreshProfile } = useAuth();
  const navigate = useNavigate();
  const fileRef = useRef(null);
  const [promptPicking, setPromptPicking] = useState(null);
  const [saving, setSaving] = useState(false);

  const [form, setForm] = useState(null);
  const [citySearch, setCitySearch] = useState('');
  const [showCityList, setShowCityList] = useState(false);

  useEffect(() => {
    if (!profile || !user) return;
    const photos = db.getPhotos(user.id).map(p => ({ url: p.url, caption: p.caption || '' }));
    const ht = profile.height_cm ? cmToFeetInches(profile.height_cm) : { feet: 5, inches: 6 };
    setForm({
      display_name: profile.display_name || '',
      last_initial: profile.last_initial || '',
      dob: profile.dob || '',
      gender: profile.gender || '',
      seeking: profile.seeking || '',
      height_mode: 'ft',
      height_feet: ht.feet,
      height_inches: ht.inches,
      height_cm_input: profile.height_cm || 168,
      city: profile.city || '',
      profession: profile.profession || '',
      education_institution: profile.education_institution || '',
      education_degree: profile.education_degree || '',
      sect: profile.sect || '',
      diet_practice: profile.diet_practice || '',
      practice_level: profile.practice_level || '',
      home_language: profile.home_language || [],
      hobbies: profile.hobbies || '',
      fav_song: profile.fav_song || '',
      fav_show: profile.fav_show || '',
      photos,
      prompts: [
        { question: profile.prompt_1_q || '', answer: profile.prompt_1_a || '' },
        { question: profile.prompt_2_q || '', answer: profile.prompt_2_a || '' },
        { question: profile.prompt_3_q || '', answer: profile.prompt_3_a || '' },
      ],
      relationship_intent: Array.isArray(profile.relationship_intent) ? profile.relationship_intent : (profile.relationship_intent ? [profile.relationship_intent] : []),
      personalityTraits: profile.personality_traits || [],
      hobbiesList: profile.hobbies_list || [],
    });
    setCitySearch(profile.city || '');
  }, [profile, user]);

  if (!form) return null;

  const u = (f, v) => setForm(p => ({ ...p, [f]: v }));
  const toggleArr = (f, item) => setForm(p => {
    const a = p[f]; return { ...p, [f]: a.includes(item) ? a.filter(i => i !== item) : [...a, item] };
  });

  const handlePhotos = async (e) => {
    const files = Array.from(e.target.files);
    e.target.value = '';
    for (const file of files) {
      if (form.photos.length >= 5) return;
      try {
        const { blob, dataUrl } = await compressImage(file);
        setForm(p => ({ ...p, photos: [...p.photos, { url: dataUrl, blob, filename: file.name, caption: '' }] }));
      } catch (err) {
        console.error('Photo compression failed:', err);
      }
    }
  };

  const removePhoto = (i) => setForm(p => ({ ...p, photos: p.photos.filter((_, idx) => idx !== i) }));
  const updateCaption = (i, c) => setForm(p => {
    const ph = [...p.photos]; ph[i] = { ...ph[i], caption: c.slice(0, 60) }; return { ...p, photos: ph };
  });

  const selectPrompt = (q) => {
    if (promptPicking === null) return;
    setForm(p => { const ps = [...p.prompts]; ps[promptPicking] = { ...ps[promptPicking], question: q }; return { ...p, prompts: ps }; });
    setPromptPicking(null);
  };

  const filteredCities = CITIES.filter(c => c.toLowerCase().includes(citySearch.toLowerCase()));
  const selectedQs = form.prompts.map(p => p.question).filter(Boolean);

  const handleSave = async () => {
    const profession = form.profession.trim();
    const degree = form.education_degree.trim();
    if (!profession || !degree) {
      toast('Profession and degree are required.');
      return;
    }

    setSaving(true);
    const heightCm = form.height_mode === 'ft' ? feetInchesToCm(form.height_feet, form.height_inches) : form.height_cm_input;
    db.updateProfile(user.id, {
      display_name: form.display_name.trim(), last_initial: form.last_initial.trim(),
      dob: form.dob, gender: form.gender, seeking: form.seeking, height_cm: heightCm,
      city: form.city, profession,
      education_institution: form.education_institution.trim(), education_degree: degree,
      sect: form.sect, diet_practice: form.diet_practice, practice_level: form.practice_level,
      home_language: form.home_language, hobbies: form.hobbies.trim(),
      fav_song: form.fav_song.trim(), fav_show: form.fav_show.trim(),
      personality_traits: form.personalityTraits,
      hobbies_list: form.hobbiesList,
      prompt_1_q: form.prompts[0].question, prompt_1_a: form.prompts[0].answer,
      prompt_2_q: form.prompts[1].question, prompt_2_a: form.prompts[1].answer,
      prompt_3_q: form.prompts[2].question, prompt_3_a: form.prompts[2].answer,
      relationship_intent: form.relationship_intent,
    });
    // Upload new photos that have blobs
    const finalPhotos = [];
    for (const p of form.photos) {
      let url = p.url;
      if (p.blob) {
        const storageUrl = await uploadToStorage(user.id, p.blob, p.filename || 'photo.jpg');
        if (storageUrl) url = storageUrl;
      }
      finalPhotos.push({ url, caption: p.caption || '' });
    }
    db.updatePhotos(user.id, finalPhotos);
    refreshProfile();
    setSaving(false);
    toast('Profile updated');
    navigate('/profile');
  };

  return (
    <div className="h-[100dvh] flex flex-col bg-[#FAF8F5] max-w-md mx-auto" data-testid="edit-profile">
      {/* Header */}
      <div className="flex items-center gap-3 px-4 py-4 border-b border-border bg-background shrink-0">
        <button onClick={() => navigate('/profile')} data-testid="edit-back"><ChevronLeft className="h-5 w-5 text-[#1A1814]" /></button>
        <h1 className="text-lg font-semibold text-[#1A1814]">Edit Profile</h1>
      </div>

      {/* Scrollable form */}
      <div className="flex-1 overflow-auto hide-scrollbar px-4 py-4">
        <Accordion type="multiple" defaultValue={['basics', 'photos']} className="space-y-2">

          {/* BASICS */}
          <AccordionItem value="basics" className="border border-border rounded-[14px] bg-white overflow-hidden">
            <AccordionTrigger className="px-4 py-3 text-sm font-semibold text-[#1A1814] hover:no-underline">Basics</AccordionTrigger>
            <AccordionContent className="px-4 pb-4 space-y-3">
              <Fld label="First name"><Input value={form.display_name} onChange={e => u('display_name', e.target.value)} className="h-11 rounded-xl bg-[#FAF8F5]" data-testid="edit-name" /></Fld>
              <Fld label="Last name"><Input value={form.last_initial} onChange={e => u('last_initial', e.target.value)} className="h-11 rounded-xl bg-[#FAF8F5]" /></Fld>
              <Fld label="Date of birth"><Input type="date" value={form.dob} onChange={e => u('dob', e.target.value)} className="h-11 rounded-xl bg-[#FAF8F5]" /></Fld>
              <Fld label="Gender">
                <div className="flex flex-wrap gap-2">
                  {['Man', 'Woman', 'Non-binary'].map(g => <Chip key={g} sel={form.gender===g} onClick={() => u('gender',g)}>{g}</Chip>)}
                </div>
              </Fld>
              <Fld label="Interested in">
                <div className="flex flex-wrap gap-2">
                  {['Men', 'Women', 'Everyone'].map(s => <Chip key={s} sel={form.seeking===s} onClick={() => u('seeking',s)}>{s}</Chip>)}
                </div>
              </Fld>
              <Fld label="Height">
                <div className="space-y-2">
                  <div className="flex gap-2">
                    <button onClick={() => u('height_mode','ft')} className={`text-xs px-3 py-1 rounded-full ${form.height_mode==='ft' ? 'bg-[#C857A8] text-white' : 'bg-[#F0EDE9]'}`}>ft/in</button>
                    <button onClick={() => u('height_mode','cm')} className={`text-xs px-3 py-1 rounded-full ${form.height_mode==='cm' ? 'bg-[#C857A8] text-white' : 'bg-[#F0EDE9]'}`}>cm</button>
                  </div>
                  {form.height_mode === 'ft' ? (
                    <div className="flex gap-2">
                      <select value={form.height_feet} onChange={e => u('height_feet',+e.target.value)} className="h-11 px-3 rounded-xl bg-[#FAF8F5] border border-border text-sm flex-1">
                        {FEET_OPTIONS.map(f => <option key={f} value={f}>{f} ft</option>)}
                      </select>
                      <select value={form.height_inches} onChange={e => u('height_inches',+e.target.value)} className="h-11 px-3 rounded-xl bg-[#FAF8F5] border border-border text-sm flex-1">
                        {INCHES_OPTIONS.map(i => <option key={i} value={i}>{i} in</option>)}
                      </select>
                    </div>
                  ) : (
                    <Input type="number" value={form.height_cm_input} onChange={e => u('height_cm_input',+e.target.value)} min={120} max={220} className="h-11 rounded-xl bg-[#FAF8F5]" />
                  )}
                </div>
              </Fld>
            </AccordionContent>
          </AccordionItem>

          {/* LOCATION & WORK */}
          <AccordionItem value="location" className="border border-border rounded-[14px] bg-white overflow-hidden">
            <AccordionTrigger className="px-4 py-3 text-sm font-semibold text-[#1A1814] hover:no-underline">Location & Work</AccordionTrigger>
            <AccordionContent className="px-4 pb-4 space-y-3">
              <div className="relative">
                <Fld label="City">
                  <Input value={form.city || citySearch} onChange={e => { setCitySearch(e.target.value); u('city',''); setShowCityList(true); }}
                    onFocus={() => setShowCityList(true)} placeholder="Search..." className="h-11 rounded-xl bg-[#FAF8F5]" />
                </Fld>
                {showCityList && filteredCities.length > 0 && (
                  <div className="absolute z-20 w-full mt-1 bg-white border border-border rounded-xl max-h-40 overflow-auto shadow-sm">
                    {filteredCities.map(c => <button key={c} onClick={() => { u('city',c); setCitySearch(c); setShowCityList(false); }} className="w-full text-left px-4 py-2 text-sm hover:bg-[#FAF8F5]">{c}</button>)}
                  </div>
                )}
              </div>
              <Fld label="Profession or field of study"><Input value={form.profession} onChange={e => u('profession',e.target.value.slice(0,60))} className="h-11 rounded-xl bg-[#FAF8F5]" /></Fld>
              <Fld label="Institution" opt><Input value={form.education_institution} onChange={e => u('education_institution',e.target.value)} className="h-11 rounded-xl bg-[#FAF8F5]" /></Fld>
              <Fld label="Degree"><Input value={form.education_degree} onChange={e => u('education_degree',e.target.value)} className="h-11 rounded-xl bg-[#FAF8F5]" /></Fld>
            </AccordionContent>
          </AccordionItem>

          {/* JAIN IDENTITY */}
          <AccordionItem value="identity" className="border border-border rounded-[14px] bg-white overflow-hidden">
            <AccordionTrigger className="px-4 py-3 text-sm font-semibold text-[#1A1814] hover:no-underline">Jain Identity</AccordionTrigger>
            <AccordionContent className="px-4 pb-4 space-y-4">
              <Fld label="Sect">
                <div className="space-y-1.5">{SECTS.map(s => <SelCard key={s.value} sel={form.sect===s.value} onClick={() => u('sect',s.value)} title={s.value} desc={s.description} />)}</div>
              </Fld>
              <Fld label="Diet practice">
                <div className="space-y-1.5">{DIET_PRACTICES.map(d => <SelCard key={d} sel={form.diet_practice===d} onClick={() => u('diet_practice',d)} title={d} />)}</div>
              </Fld>
              <Fld label="Practice level">
                <div className="space-y-1.5">{PRACTICE_LEVELS.map(p => <SelCard key={p.value} sel={form.practice_level===p.value} onClick={() => u('practice_level',p.value)} title={p.value} desc={p.description} />)}</div>
              </Fld>
            </AccordionContent>
          </AccordionItem>

          {/* PERSONALITY */}
          <AccordionItem value="personality" className="border border-border rounded-[14px] bg-white overflow-hidden">
            <AccordionTrigger className="px-4 py-3 text-sm font-semibold text-[#1A1814] hover:no-underline">Personality</AccordionTrigger>
            <AccordionContent className="px-4 pb-4 space-y-4">
              <Fld label="Languages">
                <div className="flex flex-wrap gap-2">{LANGUAGES.map(l => <Chip key={l} sel={form.home_language.includes(l)} onClick={() => toggleArr('home_language',l)}>{l}</Chip>)}</div>
              </Fld>
              <Fld label="Favourite song"><Input value={form.fav_song} onChange={e => u('fav_song',e.target.value.slice(0,60))} className="h-11 rounded-xl bg-[#FAF8F5]" /></Fld>
              <Fld label="Favourite show"><Input value={form.fav_show} onChange={e => u('fav_show',e.target.value.slice(0,60))} className="h-11 rounded-xl bg-[#FAF8F5]" /></Fld>
              <TraitsPicker
                selected={form.personalityTraits}
                onChange={v => u('personalityTraits', v)}
                title="Your personality"
                subtitle="Pick 5 that feel most like you"
              />
              <HobbiesPicker
                selected={form.hobbiesList}
                onChange={v => u('hobbiesList', v)}
                title="What you're into"
                subtitle="Pick 5 across any category"
              />
              <Fld label="What do you love doing?" opt>
                <Textarea value={form.hobbies} onChange={e => u('hobbies',e.target.value.slice(0,200))} className="min-h-[70px] rounded-xl bg-[#FAF8F5] resize-none" />
                <p className="text-right text-[10px] text-[#A8A5A0] mt-0.5">{form.hobbies.length}/200</p>
              </Fld>
            </AccordionContent>
          </AccordionItem>

          {/* PHOTOS */}
          <AccordionItem value="photos" className="border border-border rounded-[14px] bg-white overflow-hidden">
            <AccordionTrigger className="px-4 py-3 text-sm font-semibold text-[#1A1814] hover:no-underline">Photos ({form.photos.length}/5)</AccordionTrigger>
            <AccordionContent className="px-4 pb-4">
              <div className="grid grid-cols-3 gap-2">
                {form.photos.map((p, i) => (
                  <div key={i}>
                    <div className="relative aspect-[3/4] rounded-xl overflow-hidden bg-[#F0EDE9]">
                      <img src={p.url} className="w-full h-full object-cover no-screenshot" alt="" onContextMenu={(e) => e.preventDefault()} draggable={false} />
                      <button onClick={() => removePhoto(i)} className="absolute top-1 right-1 w-6 h-6 bg-black/50 rounded-full flex items-center justify-center"><X className="h-3 w-3 text-white" /></button>
                      {i === 0 && <span className="absolute bottom-1 left-1 text-[9px] bg-[#C857A8] text-white px-2 py-0.5 rounded-full">Cover</span>}
                    </div>
                    <Input value={p.caption} onChange={e => updateCaption(i,e.target.value)} placeholder="Caption" className="mt-1 h-7 text-xs rounded-lg bg-[#FAF8F5]" maxLength={100} />
                  </div>
                ))}
                {form.photos.length < 5 && (
                  <button onClick={() => fileRef.current?.click()} className="aspect-[3/4] rounded-xl border-2 border-dashed border-[rgba(0,0,0,0.10)] flex flex-col items-center justify-center gap-1" data-testid="edit-add-photo">
                    <Camera className="h-5 w-5 text-[#A8A5A0]" /><span className="text-[10px] text-[#A8A5A0]">Add</span>
                  </button>
                )}
              </div>
              <input ref={fileRef} type="file" accept="image/*" multiple className="hidden" onChange={handlePhotos} />
            </AccordionContent>
          </AccordionItem>

          {/* PROMPTS */}
          <AccordionItem value="prompts" className="border border-border rounded-[14px] bg-white overflow-hidden">
            <AccordionTrigger className="px-4 py-3 text-sm font-semibold text-[#1A1814] hover:no-underline">Prompts</AccordionTrigger>
            <AccordionContent className="px-4 pb-4 space-y-3">
              {form.prompts.map((p, i) => (
                <div key={i} className="p-3 rounded-[12px] bg-[#F7F5F2]">
                  {p.question ? (
                    <>
                      <div className="flex items-start justify-between gap-2">
                        <p className="text-[10px] uppercase tracking-[0.08em] text-[#A8A5A0] font-medium flex-1">{p.question}</p>
                        <button onClick={() => { const ps=[...form.prompts]; ps[i]={question:'',answer:''}; u('prompts',ps); }}><X className="h-3.5 w-3.5 text-[#A8A5A0]" /></button>
                      </div>
                      <Textarea value={p.answer} onChange={e => { const ps=[...form.prompts]; ps[i]={...ps[i],answer:e.target.value.slice(0,150)}; u('prompts',ps); }}
                        className="mt-2 min-h-[50px] resize-none rounded-lg text-sm bg-white" />
                      <p className="text-right text-[10px] text-[#A8A5A0]">{p.answer.length}/150</p>
                    </>
                  ) : (
                    <button onClick={() => setPromptPicking(i)} className="text-sm text-[#A8A5A0] hover:text-[#1A1814]">
                      <Plus className="h-4 w-4 inline mr-1" />Select prompt {i+1}
                    </button>
                  )}
                </div>
              ))}
            </AccordionContent>
          </AccordionItem>

          {/* LOOKING FOR */}
          <AccordionItem value="intent" className="border border-border rounded-[14px] bg-white overflow-hidden">
            <AccordionTrigger className="px-4 py-3 text-sm font-semibold text-[#1A1814] hover:no-underline">Looking for</AccordionTrigger>
            <AccordionContent className="px-4 pb-4 space-y-2">
              {RELATIONSHIP_INTENTS.map(ri => (
                <button key={ri} onClick={() => toggleArr('relationship_intent',ri)}
                  className={`w-full text-left p-3 rounded-xl border text-sm ${form.relationship_intent.includes(ri) ? 'border-[#C857A8] bg-saffron-light font-medium' : 'border-[rgba(0,0,0,0.10)] bg-[#FAF8F5]'} text-[#1A1814]`}>
                  {ri}{form.relationship_intent.includes(ri) && <Check className="inline h-4 w-4 text-[#C857A8] ml-1.5" />}
                </button>
              ))}
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      </div>

      {/* Save button */}
      <div className="px-4 pb-6 pt-2 shrink-0">
        <Button onClick={handleSave} disabled={saving} className="w-full h-[52px] text-[15px] font-semibold rounded-[26px] bg-[#C857A8] hover:bg-saffron-mid" data-testid="save-profile-btn">
          {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
          {saving ? 'Saving...' : 'Save Changes'}
        </Button>
      </div>

      {/* Prompt picker overlay */}
      {promptPicking !== null && (
        <div className="fixed inset-0 z-50 bg-[#FAF8F5] flex flex-col">
          <div className="flex items-center justify-between p-4 border-b border-border">
            <h3 className="font-semibold text-[#1A1814]">Pick a prompt</h3>
            <button onClick={() => setPromptPicking(null)}><X className="h-5 w-5" /></button>
          </div>
          <div className="flex-1 overflow-auto p-4 space-y-4">
            <div>
              <p className="text-[10px] uppercase tracking-[0.08em] text-[#A8A5A0] font-medium mb-2">General</p>
              {GENERAL_PROMPTS.filter(p => !selectedQs.includes(p)).map(p => (
                <button key={p} onClick={() => selectPrompt(p)} className="block w-full text-left py-2.5 text-sm text-[#1A1814] border-b border-[rgba(0,0,0,0.05)] hover:text-[#C857A8]">{p}</button>
              ))}
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-[0.08em] text-[#A8A5A0] font-medium mb-2">Jain-specific</p>
              {JAIN_PROMPTS.filter(p => !selectedQs.includes(p)).map(p => (
                <button key={p} onClick={() => selectPrompt(p)} className="block w-full text-left py-2.5 text-sm text-[#1A1814] border-b border-[rgba(0,0,0,0.05)] hover:text-[#C857A8]">{p}</button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Fld({ label, opt, children }) {
  return (
    <div>
      <label className="text-[11px] uppercase tracking-wider text-[#A8A5A0] font-medium mb-1 block">
        {label}{opt && <span className="normal-case tracking-normal"> (optional)</span>}
      </label>
      {children}
    </div>
  );
}

function Chip({ sel, onClick, children }) {
  return (
    <button onClick={onClick} className={`px-3 py-1.5 rounded-full border text-sm ${sel ? 'border-[#C857A8] bg-[#C857A8] text-white' : 'border-[rgba(0,0,0,0.10)] bg-[#FAF8F5] text-[#1A1814]'}`}>
      {children}
    </button>
  );
}

function SelCard({ sel, onClick, title, desc }) {
  return (
    <button onClick={onClick} className={`w-full text-left p-2.5 rounded-xl border text-sm ${sel ? 'border-[#C857A8] bg-saffron-light' : 'border-[rgba(0,0,0,0.10)] bg-[#FAF8F5]'}`}>
      <span className="font-medium text-[#1A1814]">{title}</span>
      {desc && <p className="text-xs text-[#A8A5A0] mt-0.5">{desc}</p>}
      {sel && <Check className="inline h-3.5 w-3.5 text-[#C857A8] ml-1" />}
    </button>
  );
}
